import API_CONFIG from "../../../config/apiconfig";

/** Resolve stored document/blob path to a full https URL. */
export function buildDocumentDownloadUrl(documentUrl) {
  const raw = String(documentUrl ?? "").trim();
  if (!raw) return "";

  const base = String(API_CONFIG.blobBaseURL || "").replace(/\/+$/, "");

  const dedupe = (url) => {
    if (!url || !base) return url;
    const baseHostPath = base.replace(/^https?:\/\//i, "");
    const duplicatePrefix = `${base}/${baseHostPath}/`;
    if (url.toLowerCase().startsWith(duplicatePrefix.toLowerCase())) {
      return `${base}/${url.slice(duplicatePrefix.length)}`;
    }
    return url;
  };

  if (/^https?:\/\//i.test(raw)) return dedupe(raw);
  if (raw.startsWith("//")) return dedupe(`https:${raw}`);
  if (/^[a-z0-9.-]+\.(blob\.core\.windows\.net|azurewebsites\.net)\//i.test(raw)) {
    return dedupe(`https://${raw}`);
  }
  if (!base) return raw;
  return dedupe(`${base}/${raw.replace(/^\/+/, "")}`);
}

export function normalizeFileExtension(value) {
  return String(value || "")
    .trim()
    .replace(/^\./, "")
    .toLowerCase();
}

/** Prefer stored extension; otherwise infer from URL / file name. */
export function inferFileExtension(
  documentExtension,
  documentUrl = "",
  documentTitle = "",
) {
  const fromMeta = normalizeFileExtension(documentExtension);
  if (fromMeta && /^[a-z0-9]{1,10}$/.test(fromMeta)) return fromMeta;

  const fromPath = (path) => {
    const base =
      String(path || "")
        .split("?")[0]
        .split("#")[0]
        .split("/")
        .pop() || "";
    const match = base.match(/\.([a-z0-9]{1,10})$/i);
    return match ? normalizeFileExtension(match[1]) : "";
  };

  return fromPath(documentUrl) || fromPath(documentTitle) || "";
}

/**
 * Safe leaf name for Content-Disposition / browser PDF toolbar.
 * Avoids path segments and characters that break the header (Chrome then shows "Inline").
 */
export function sanitizePreviewFileName(fileName, fallback = "document.pdf") {
  let name = String(fileName || "")
    .trim()
    .replace(/[/\\?%*:|"<>]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/["']/g, "")
    .replace(/\.+/g, ".")
    .replace(/^\.+/, "")
    .slice(0, 180)
    .trim();

  if (!name || /^inline$/i.test(name)) {
    name = String(fallback || "document.pdf").trim() || "document.pdf";
  }
  if (!/\.[a-z0-9]{1,10}$/i.test(name)) {
    name = `${name}.pdf`;
  }
  return name;
}

/**
 * URL PDF.js can fetch without Azure blob CORS.
 * Local blob/data URLs stay as-is; remote blobs go through the API Inline proxy
 * (same path as QMS / eISF DocumentPreviewPane).
 */
export function buildPdfJsPreviewUrl(documentUrl, fileName = "") {
  const raw = String(documentUrl || "").trim();
  if (!raw) return "";
  if (raw.startsWith("blob:") || raw.startsWith("data:")) return raw;
  return buildInlinePreviewProxyUrl(raw, fileName) || buildDocumentDownloadUrl(raw);
}

/**
 * API proxy that streams the blob with Content-Disposition: inline.
 * Use this as iframe/img/PDF.js src so Azure "attachment" blobs still preview.
 * Pass fileName so the browser PDF toolbar shows the real document name (not "Inline").
 */
export function buildInlinePreviewProxyUrl(documentUrl, fileName = "") {
  const url = buildDocumentDownloadUrl(documentUrl);
  if (!url) return "";
  const base = String(API_CONFIG.baseURL || "").replace(/\/?$/, "/");
  const safeName = sanitizePreviewFileName(fileName);
  const params = new URLSearchParams();
  params.set("url", url);
  params.set("fileName", safeName);
  return `${base}api/DocumentPreview/Inline?${params.toString()}`;
}

/**
 * Prefer API inline proxy (no download). Fallback: fetch → blob: URL.
 */
export async function createInlineObjectUrl(
  remoteUrl,
  mimeType = "application/pdf",
  fileName = "",
) {
  const proxyUrl = buildInlinePreviewProxyUrl(remoteUrl, fileName);
  if (proxyUrl) {
    // Proxy already sends inline headers — use directly in iframe/img.
    return proxyUrl;
  }

  const url = buildDocumentDownloadUrl(remoteUrl);
  if (!url) return "";

  const response = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!response.ok) {
    throw new Error(`Unable to load file (${response.status})`);
  }

  const buffer = await response.arrayBuffer();
  return URL.createObjectURL(
    new Blob([buffer], { type: mimeType || "application/pdf" }),
  );
}

export async function downloadOriginalDocument({ documentUrl, fileName }) {
  const url = buildDocumentDownloadUrl(documentUrl);
  if (!url) return false;

  const name = String(fileName || "document").trim() || "document";

  // Prefer blob download so cross-origin Azure links don't open a new tab.
  try {
    const response = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!response.ok) throw new Error("fetch failed");
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return true;
  } catch {
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  }
}

export function buildFileTitle(displayName, extension) {
  const name = String(displayName || "Document").trim() || "Document";
  const ext = normalizeFileExtension(extension);
  if (!ext) return name;
  if (name.toLowerCase().endsWith(`.${ext}`)) return name;
  return `${name}.${ext}`;
}
